import type { DocumentDefinition } from "@repo/models";

import { clone, hasValue, noValue } from "@repo/helpers";
import { SignatureType } from "@repo/models";

import type { component, section } from "../models/component";
import type { documentReview } from "../models/documentReview";
import type { MappedComponent, MappedDyno, MappedSection } from "../models/mappedComponent";

import { componentType, sectionType } from "../models/enums";
import { localizeTraversedName, traverseProperty, traversePropertyFn } from "./dynamicProperties";
import { Templator } from "./template";
import { getSectionConditionalFor, matchConditional, shouldBeDisplayed } from "./validation";

export const byOrdinal = (a: MappedDyno, b: MappedDyno) => a.ordinal - b.ordinal || a.id - b.id;
export const isVisible = (a: MappedDyno) => a.visible;

export class SectionMapping<TProps extends Props> {
	mapSection =
		(props: TProps, parentPropertyName?: string, iteratedTotal?: number) => (section: section, index?: number) =>
			this.mapSectionInternal(props, section, parentPropertyName, iteratedTotal, index);

	protected mapSectionInternal(
		props: TProps,
		section: section,
		parentPropertyName?: string,
		iteratedTotal?: number,
		index?: number,
	) {
		props = { ...props, index };
		const propertyName = localizeTraversedName(section.propertyName, parentPropertyName);
		const propertyValue = getDerivedValue(props, parentPropertyName)(propertyName);
		const mappedSection = {
			label: getValueOrDerived(props, section.name, parentPropertyName),
			id: section.id,
			ordinal: section.ordinal,
			propertyName,
			propertyValue,
			childSections: [],
			components: [],
			visibleFor: getSectionConditionalFor(parentPropertyName, section as any).visibleFor,
			iteratedTotal,
			section,
			subSectionLayout: section.subSectionLayout,
			options: section.options,
		} as MappedSection;
		if (Array.isArray(propertyValue)) {
			const mapIteratedSection = this.mapSection(props, propertyName, propertyValue.length);

			let name = section.iteratedName ? section.iteratedName : section.name;
			//Only display count of iterated (like "Service Location 2") if there's more than 1
			if (propertyValue.length === 1) name = name.replace(" #=index#", "");

			name = this.mapIteratedChildSectionName(name, section.type);

			mappedSection.childSections = propertyValue.map((o, i) =>
				mapIteratedSection(
					{
						...section,
						propertyName: `${propertyName}[${i}]`,
						name,
					},
					i,
				),
			);
			mappedSection.label = getValueOrDerived(props, section.name, parentPropertyName);
		} else {
			const childSections = section.childSections.map((s) => this.mapSection(props, propertyName)(s, index));
			const childComponents = section.components.map(this.mapComponent(props, propertyName));

			mappedSection.childSections = childSections;
			mappedSection.components = childComponents;
		}
		mappedSection.visible = shouldBeDisplayed(props)(mappedSection);

		mappedSection.addRemoveSectionHidden =
			hasValue(mappedSection.options?.addRemoveSectionHidden) &&
			matchConditional(mappedSection.options.addRemoveSectionHidden, props);
		mappedSection.linkName = mappedSection.id + (mappedSection.propertyName || "") + mappedSection.label;

		if (mappedSection?.options?.completionStatus?.show) {
			const ignoredFields = mappedSection.options.completionStatus.ignoredFields;
			const componentValues = mappedSection.childSections
				.flatMap((s) => s.childSections)
				.flatMap((s) => s.components)
				.filter((c) => !ignoredFields.some((f) => c.propertyName?.endsWith(`.${f}`)))
				.map((c) => c.propertyValue);
			const pass = componentValues.every((v) => v);
			const incomplete = componentValues.some(noValue);
			mappedSection.status = pass ? "Pass" : incomplete ? "Incomplete" : "Fail";
		}

		return mappedSection;
	}

	mapComponent = (props: TProps, parentPropertyName?: string) => (component: component) =>
		this.mapComponentInternal(props, component, parentPropertyName);

	protected mapIteratedChildSectionName(name: string, type: sectionType) {
		//Only display child section name if parent section is iterated
		//MSC - this was causing problems with BYOD contracts, so removed
		//if (!ITERATED_SECTION_TYPES.includes(section.type)) name = "";
		return name;
	}

	/**
	 * Maps component to mappedComponent
	 * @param props
	 * @param component
	 * @param parentPropertyName
	 * @returns
	 */
	protected mapComponentInternal(props: TProps, component: component, parentPropertyName?: string) {
		const parseDerivedInt = (num: string) =>
			parseInt(getValueOrDerived(props, num, parentPropertyName) || "0") || null;
		const parseDerivedFloat = (num: string) =>
			parseFloat(getValueOrDerived(props, num, parentPropertyName) || "0") || null;
		const mappedComponent = {
			label: getValueOrDerived(props, component.name, parentPropertyName),
			id: component.id,
			ordinal: component.ordinal,
			column: component.column ?? -1,
			//Maybe that splitting should be a function
			propertyName:
				component.propertyName &&
				component.propertyName
					.split(",")
					.map((name) => localizeTraversedName(name, parentPropertyName))
					.join(","),
			propertyValue: this.getDerivedValueComponent(props, parentPropertyName)(component),
			extensionPropertyName: localizeTraversedName(component.extensionPropertyName, parentPropertyName),
			extensionPropertyValue: getDerivedValue(props, parentPropertyName)(component.extensionPropertyName),
			extendedProperties: {
				regex: getDerivedRegex(component.regex, props, parentPropertyName),
				min: parseDerivedFloat(component.min),
				max: parseDerivedInt(component.max),
				minLength: parseDerivedInt(component.min),
				maxLength: parseDerivedInt(component.max),
				inline: component.inline,
				prefix: getValueOrDerived(props, component.prefix, parentPropertyName),
				selectIfSingle: component.selectorOptions?.selectIfSingle,
				autoSelect: component.selectorOptions?.autoSelect,
				multiSelect: component.selectorOptions?.multiSelect,
				useAllDisplay: component.selectorOptions?.useAllDisplay,
				useRootPropertyName: component.selectorOptions?.useRootPropertyName,
				hasOtherField: component.selectorOptions?.hasOtherField,
				isOtherSelected:
					getDerivedValue(props, parentPropertyName)(component.propertyName + "Name")?.toLowerCase() ===
					"other",
				hasUrl: component.selectorOptions?.hasUrl,
				explicitOptions: component.selectorOptions?.explicitOptions,
				size: component.selectorOptions?.size,
				validationMessage: component.validationMessage,
			},
			required: component.required,
			readOnly: component.readOnly,
			optional: getDerivedOptional(props, parentPropertyName, component),
			optionalLoaded: getDerivedOptionalLoaded(props, parentPropertyName, component),
			styleOptions: component.styleOptions,
			type: component.type,
			...getSectionConditionalFor(parentPropertyName, component),
			confirm: component.confirm,
			component,
		} as MappedComponent;

		//TO REMOVE
		mappedComponent.hasUrl = mappedComponent.extendedProperties.hasUrl;
		mappedComponent.visible = shouldBeDisplayed(props)(mappedComponent);
		mappedComponent.required = mappedComponent.required || matchConditional(mappedComponent.requiredFor, props);
		mappedComponent.disabled = matchConditional(mappedComponent.disabledFor, props);
		mappedComponent.linkName = mappedComponent.propertyName;
		mappedComponent.url = getAsyncUrl(props, this.getHasUrl(mappedComponent), component.value, parentPropertyName);

		//No generic component has confirmation right now
		if (mappedComponent.hasConfirmation) {
			mappedComponent.confirmationPropertyValue =
				props.getConfirmationProperty && props.getConfirmationProperty(mappedComponent.propertyName);
			// Confirmation prop isn't set first time around, so match by default
			// (when confirmation property updates, this will as well)
			if (mappedComponent.confirmationPropertyValue === undefined)
				mappedComponent.confirmationPropertyValue = mappedComponent.propertyValue;
		}
		switch (mappedComponent.type) {
			case componentType.entryAddress:
				this.mapAddressComponents(mappedComponent, props);
				break;
			case componentType.entrySignDocument:
				this.mapSignDocumentComponents(mappedComponent, props);
				break;
			case componentType.bulletedTemplate:
				this.mapBulletedTemplateComponents(mappedComponent, props);
				break;
			case componentType.entryDropdown:
			case componentType.panelSelector:
			case componentType.radioButtonList:
				if (!component.selectorOptions?.useRootPropertyName) mappedComponent.linkName += "Name";
				this.mapSelectorOther(mappedComponent, props, parentPropertyName);
				break;
			case componentType.entryTextWithLookup:
				this.mapEntryTextWithLookupComponents(mappedComponent);
				break;
			case componentType.entryTextWithValidation:
				this.mapEntryTextWithValidation(mappedComponent, props, parentPropertyName);
				break;
			default:
				break;
		}

		//TODO: fully update this (currently only fully implemented in BYOD)

		return mappedComponent;
	}

	/**
	 * Address gets mapped as a container for child components.  If there is a list of states,
	 * inheriting class needs to define getStates
	 * @param addressComponent
	 * @param props
	 * @param zipReadyOnly
	 * @param cityReadOnly
	 * @param stateReadOnly
	 */
	protected mapAddressComponents(
		addressComponent: MappedComponent,
		props: TProps,
		zipReadyOnly = false,
		cityReadOnly = true,
		stateReadOnly = true,
	) {
		const address = addressComponent.propertyValue || {};
		const addressMatches =
			address.matches &&
			hasValue(addressComponent.component.copiedFromName) &&
			hasValue(addressComponent.component.copiedFromProperty);

		const getSubComponent = (propertyName: string, name: string, props?: Partial<MappedComponent>) => {
			const subComponent = {
				...clone(addressComponent),
				type: componentType.entryText,
				readOnly: addressMatches,
				...props,
			};

			subComponent.propertyName = `${addressComponent.propertyName}.${propertyName}`;
			subComponent.propertyValue = address[propertyName];
			if (subComponent.type === componentType.entryDropdown)
				subComponent.propertyValue = address[propertyName + "Id"];

			subComponent.label = name;
			subComponent.linkName = propertyName;
			if (subComponent.type === componentType.entryDropdown) subComponent.linkName = propertyName + "Name";

			return subComponent;
		};
		const address1 = getSubComponent("address1", "Address 1");
		const address2 = getSubComponent("address2", "Address 2", {
			required: false,
		});
		const city = getSubComponent("city", "City", {
			type: componentType.entryText,
			readOnly: cityReadOnly || addressMatches || addressComponent.readOnly,
		});
		const states = this.getStates(props);
		const state = states
			? getSubComponent("state", "State", {
					optional: states,
					type: componentType.entryDropdown,
					readOnly: addressMatches || addressComponent.readOnly || stateReadOnly,
				})
			: getSubComponent("state", "State", { type: componentType.entryText });
		const zip = getSubComponent("zip", "Zip", {
			type: componentType.entryZip,
			readOnly: addressMatches || addressComponent.readOnly || zipReadyOnly,
		});

		addressComponent.components = addressComponent.readOnly
			? [address1, address2, city, state, zip]
			: [address1, address2, zip, city, state];
	}

	protected mapBulletedTemplateComponents(bulletedTemplateComponent: MappedComponent, props: TProps) {
		const getSubComponent = (value: string) => {
			return {
				...clone(bulletedTemplateComponent),
				type: componentType.template,
				component: {
					...bulletedTemplateComponent.component,
					value: value,
				},
			};
		};

		bulletedTemplateComponent.components = JSON.parse(bulletedTemplateComponent.component.value).map(
			getSubComponent,
		);
	}

	protected mapSignDocumentComponents(signDocComponent: MappedComponent, props: TProps) {
		const docReview = (signDocComponent.propertyValue || {}) as documentReview;
		const definition = signDocComponent.optional as DocumentDefinition;
		if (!definition) return;
		const getSubComponent = (
			propertyName: keyof documentReview,
			type: componentType,
			label?: string,
			props?: Partial<MappedComponent>,
		) => {
			const subComponent = {
				...clone(signDocComponent),
				type,
				...props,
			};

			subComponent.propertyName = `${signDocComponent.propertyName}.${propertyName}`;
			subComponent.propertyValue = docReview[propertyName];
			subComponent.label = label || signDocComponent.label;

			return subComponent;
		};
		let topLabel = definition.title;
		if (definition.descriptor) topLabel += ` (${definition.descriptor})`;
		const document = getSubComponent("reviewed", componentType.documentReview, topLabel);
		const signature = getSubComponent(
			"signature",
			componentType.entrySignature,
			definition.signatureText || "Sign Here",
			{ readOnly: !docReview.reviewed },
		);
		const check = getSubComponent("checked", componentType.entryBoolean, definition.signatureText || "Check Here", {
			readOnly: !docReview.reviewed,
		});
		const message = {
			...clone(signDocComponent),
			type: componentType.text,
			component: { ...signDocComponent.component, type: componentType.text, value: "Scroll to bottom to review" },
		} as MappedComponent;

		signDocComponent.components = [document];
		switch (definition.signatureType) {
			case SignatureType.signature:
				signDocComponent.components.push(message);
				signDocComponent.components.push(signature);
				break;
			case SignatureType.check:
				signDocComponent.components.push(message);
				signDocComponent.components.push(check);
				break;
		}
	}

	protected mapSelectorOther(parentComponent: MappedComponent, props: TProps, parentPropertyName?: string) {
		if (!parentComponent.propertyValue) return;
		if (!parentComponent.extendedProperties.hasOtherField) return;
		if (!parentComponent.extendedProperties.isOtherSelected) return;

		parentComponent.components = [
			{
				...clone(parentComponent),
				label: `Other ${parentComponent.label}`,
				type: componentType.entryText,
				propertyName: parentComponent.extensionPropertyName,
				propertyValue: parentComponent.extensionPropertyValue,
				visible: true,
				required: true,
				extendedProperties: clone(parentComponent.extendedProperties),
			} as MappedComponent,
		];
	}

	protected mapEntryTextWithLookupComponents(component: MappedComponent) {
		const [entryTextComponentLabel, buttonLabel] = component.label.split(",");

		const entryTextComponent = {
			...clone(component),
			label: entryTextComponentLabel,
			type: componentType.entryText,
			propertyName: component.propertyName,
			propertyValue: component.propertyValue,
			visibleFor: null,
			extensionPropertyName: null,
			extensionPropertyValue: null,
			extendedProperties: { ...component.extendedProperties, prefix: null },
			component: {
				...component.component,
				value: null,
				type: componentType.entryText,
			},
		};

		const lookupButton = {
			...clone(component),
			label: buttonLabel,
			type: componentType.button,
			component: { ...component.component, type: componentType.button },
		};

		component.components = [entryTextComponent, lookupButton];
	}

	protected mapEntryTextWithValidation(component: MappedComponent, props: TProps, parentPropertyName?: string) {
		const propName = component.propertyName + "Validation";
		component.extensionPropertyName = localizeTraversedName(propName, parentPropertyName);
		component.extensionPropertyValue = getDerivedValue(props, parentPropertyName)(propName);
	}

	protected getStates(props: TProps) {
		return null;
	}

	protected getHasUrl(mappedComponent: MappedComponent) {
		return mappedComponent.hasUrl;
	}

	protected getDerivedValueComponent =
		(props: TProps, parentPropertyName: string, propertyName?: string) => (component: component) =>
			this.getDerivedValueComponentInternal(
				props,
				parentPropertyName,
				component,
				propertyName || component.propertyName,
			);
	protected getDerivedValueComponentInternal(
		props: TProps,
		parentPropertyName: string,
		component: component,
		propertyName: string,
	) {
		const getDerivedValueLocal = getDerivedValue(props, parentPropertyName);
		switch (component.type) {
			case componentType.entryDropdown:
			case componentType.panelSelector:
			case componentType.radioButtonList:
				if (!component.selectorOptions?.useRootPropertyName) propertyName += "Id";
				break;
			case componentType.fileUpload:
				return {
					values: getDerivedValueLocal(propertyName),
					uploadedValues: getDerivedValueLocal(propertyName + "Uploaded"),
				};
		}
		return getDerivedValueLocal(propertyName);
	}
}

export function getDerivedValue<TProps>(props: TProps, parentPropertyName: string) {
	return function (propertyName: string) {
		propertyName = localizeTraversedName(propertyName, parentPropertyName);
		return traverseProperty<any>(propertyName, props, parentPropertyName);
	};
}

export function getDerivedRegex<TProps>(regex: string, props?: TProps, parentPropertyName?: string) {
	if (!regex) return null;
	if (regex.startsWith("prop:")) {
		regex = localizeTraversedName(regex, parentPropertyName).replace("prop:", "");
		const def = traverseProperty<string>(regex, props, parentPropertyName);
		return def && new RegExp(def);
	} else {
		return new RegExp(regex.replace(/\\\\/g, "\\").replace(/"/g, ""));
	}
}

/**
 * Returns derived value, if templated.  Else returns straight value
 * @param props
 * @param value
 * @param parentPropertyName
 * @returns
 */
function getValueOrDerived<TProps>(props: TProps, value: string, parentPropertyName?: string) {
	if (!value) return "";
	if (value.startsWith("prop:")) {
		value = value.replace("prop:", "");
		value = localizeTraversedName(value, parentPropertyName);
		value = traverseProperty<string>(value, props);
	} else {
		value = Templator(props, parentPropertyName, value, props["index"]).fill();
	}
	return value;
}

function getAsyncUrl<TProps>(props: TProps, hasUrl: boolean, value: string, parentPropertyName?: string) {
	if (!hasUrl) return null;
	return Templator(props, parentPropertyName, value, null, false).fill();
}

//Gets dropdown options for dropdown selector.tsx
export function getDerivedOptional<TProps>(props: TProps, parentPropertyName: string, component: component) {
	if (!component.value) return null;

	if (component.selectorOptions?.explicitOptions) return JSON.parse(component.value);

	const mapped = component.value.split(",").map(traversePropertyFn<string>(props, parentPropertyName));

	if (!mapped) return null;
	if (mapped.length === 1)
		//If the optional "value" field isn't split, just return it straight up
		return mapped[0];
	else return mapped;
}
//Gets dropdown options for dropdown selector.tsx
export function getDerivedOptionalLoaded<TProps extends { loaded?: boolean | undefined }>(
	props: TProps,
	parentPropertyName: string,
	component: component,
) {
	if (!component.value) return null;
	if (!props.loaded) return false;
	const mapped = component.value
		.split(",")
		.map((v) => `${v}Loaded`)
		.map(traversePropertyFn<string>(props, parentPropertyName));

	for (const item of mapped) {
		if (!hasValue(item)) return null;
		if (!item) return false;
	}
	return true;
}

export interface Props {
	getConfirmationProperty?: (id: string | number) => string;
	loaded?: boolean | undefined;
}
